import { useCallback, useMemo, useState } from 'react';

import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import useCoupons, {
  DEFAULT_COUPONS_PAGE_SIZE,
  CouponsFilters,
  getCouponsIsActiveFilter
} from 'api/useCoupons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import DataTable from 'components/DataTableComponent/DataTable';
import EmptyState from 'components/EmptyState/EmptyState';
import { TABLE_VIEW_DEFAULTS } from 'components/TableView/TableView.constants';

import { EToasterStatus, showToast } from '../../utils/showToast';

import { COUPONS_TABLE_COLUMNS } from './constants/coupons';
import { hasAppliedCouponsFilters } from './hooks/useCouponsFilters';
import { CouponsOutletContext } from './types';
import { isDatePassed } from './utils';

dayjs.extend(isSameOrAfter);

const CouponsTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const {
    couponNameValue,
    promoCodeValue,
    discountValue,
    statusValues,
    rangeDates,
    isDisabledDueToPermissions
  } = useOutletContext<CouponsOutletContext>();

  const hasAppliedFilters = hasAppliedCouponsFilters({
    couponNameValue,
    promoCodeValue,
    discountValue,
    statusValues,
    rangeDates
  });

  const statusFilterKey = statusValues.join(',');
  const [prevStatusFilterKey, setPrevStatusFilterKey] =
    useState(statusFilterKey);

  if (prevStatusFilterKey !== statusFilterKey) {
    setPrevStatusFilterKey(statusFilterKey);
    setCurrentPage(0);
  }

  const couponsFilters = useMemo(
    (): CouponsFilters => ({
      page: currentPage + 1,
      limit: DEFAULT_COUPONS_PAGE_SIZE,
      isActive: getCouponsIsActiveFilter(statusValues)
    }),
    [currentPage, statusValues]
  );

  const { getCoupons, setCouponActive } = useCoupons({
    publisherId,
    filters: couponsFilters
  });

  const toggleActivateCoupon = useCallback(
    async (couponId: string, active: boolean) => {
      setCouponActive.mutate(
        {
          couponId,
          active
        },
        {
          onSuccess: () => {
            showToast({
              message: `Coupon ${active ? 'activated' : 'deactivated'} successfully`,
              status: EToasterStatus.SUCCESS
            });
            getCoupons.refetch();
          },
          onError: (error: any) => {
            showToast({
              message: `Failed to ${active ? 'activate' : 'deactivate'} coupon`,
              status: EToasterStatus.ERROR,
              description: error.message || ''
            });
          }
        }
      );
    },
    [setCouponActive, getCoupons]
  );

  const isCouponExpired = useCallback((expiredBy: string) => {
    if (!expiredBy) {
      return false;
    }
    return isDatePassed(expiredBy);
  }, []);

  const columns = useMemo(() => {
    return COUPONS_TABLE_COLUMNS({
      navigate,
      toggleActivateCoupon,
      isDisabledDueToPermissions,
      isCouponExpired
    });
  }, [
    navigate,
    toggleActivateCoupon,
    isDisabledDueToPermissions,
    isCouponExpired
  ]);

  const pageCoupons = getCoupons.data?.data ?? [];
  const totalPages = getCoupons.data?.totalPages ?? 0;

  const filteredCoupons = useMemo(() => {
    let rows = pageCoupons;

    if (couponNameValue !== '') {
      rows = rows.filter((row) =>
        row.name.toLowerCase().includes(couponNameValue.toLowerCase())
      );
    }

    if (promoCodeValue !== '') {
      rows = rows.filter((row) =>
        row.promoCodeNames?.some((promoCodeName) =>
          promoCodeName.toLowerCase().includes(promoCodeValue.toLowerCase())
        )
      );
    }

    if (discountValue !== '') {
      rows = rows.filter(
        (row) => row.discountPercentage === Number(discountValue)
      );
    }

    if (rangeDates?.length) {
      const [from, to] = rangeDates;
      rows = rows.filter((row) => {
        const isAfterStartDay = dayjs(row.createdAt).isSameOrAfter(from, 'day');
        const isBeforeEndDay =
          dayjs(row.createdAt).isBefore(to, 'day') ||
          dayjs(row.createdAt).isSame(to, 'day');
        return isAfterStartDay && isBeforeEndDay;
      });
    }

    return rows;
  }, [pageCoupons, couponNameValue, promoCodeValue, discountValue, rangeDates]);

  const isLoading = getCoupons.isLoading || getCoupons.isFetching;
  const showNoMatchesEmptyState =
    hasAppliedFilters && filteredCoupons.length === 0 && !isLoading;

  if (showNoMatchesEmptyState) {
    return (
      <div className="flex items-center justify-center w-full min-h-[calc(100vh-350px)]">
        <EmptyState
          title="No exact matches"
          description="Try changing or removing some of your filters or adjusting your search area."
          icon="fa-regular fa-filter"
          hideButton
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full w-full">
      <div className="flex flex-col gap-4 border border-base-border rounded-lg p-4 border-b">
        <div className="flex justify-between items-center pl-3 pb-1">
          <div className="font-medium text-lg text-base-primary">
            Coupons List
          </div>
        </div>

        <div className="overflow-hidden flex flex-col max-h-[calc(100vh-280px)] min-h-0 w-full">
          <div className="overflow-y-auto flex-1 w-full">
            <DataTable
              className="w-full"
              key={`table-${filteredCoupons.length}`}
              enableColumnResizing={TABLE_VIEW_DEFAULTS.ENABLE_COLUMN_RESIZING}
              stickyHeader={TABLE_VIEW_DEFAULTS.STICKY_HEADER}
              data={filteredCoupons}
              columns={columns}
              rowIdIdentifier={TABLE_VIEW_DEFAULTS.ROW_ID}
              isLoading={isLoading}
              showPagination={totalPages > 1}
              rowsPerPage={DEFAULT_COUPONS_PAGE_SIZE}
              manualPagination
              pageIndex={currentPage}
              pageCount={totalPages}
              onPageChange={setCurrentPage}
              rowCount={getCoupons.data?.totalCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponsTable;
